import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Drawer,
  IconButton,
  Divider,
  Typography,
  Collapse,
} from '@mui/material';
import {
  FaTachometerAlt,
  FaIdBadge,
  FaUserCircle,
  FaCalendarAlt,
  FaShoppingCart,
  FaClipboardList,
  FaKey,
  FaBoxes,
  FaUserPlus,
} from 'react-icons/fa';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import MenuIcon from '@mui/icons-material/Menu';
import logo from '../../assets/logo.svg';

function Sidebar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const location = useLocation();

  const menuGroups = [
    {
      category: "Main Navigation",
      icon: <FaTachometerAlt />,
      items: [
        { text: 'Dashboard', icon: <FaTachometerAlt />, link: '/Dashboard_store_keeper' },
      ]
    },
    {
      category: "Staff Management",
      icon: <FaUserCircle />,
      items: [
        { text: 'Onboard Me', icon: <FaUserPlus />, link: '#' },
        { text: 'My Profile', icon: <FaUserCircle />, link: '/StaffProfileView' },
        { text: 'Change Password', icon: <FaKey />, link: '/PasswordChange_storekeeper' },
      ]
    },
    {
      category: "Inventory & Items",
      icon: <FaBoxes />,
      items: [
        { text: 'Create Store Item', icon: <FaIdBadge />, link: '/ItemNamesManagement' },
        { text: 'Inventory & GRN', icon: <FaBoxes />, link: '/InventoryManagement' },
      ]
    },
    {
      category: "Orders & Requests",
      icon: <FaShoppingCart />,
      items: [
        { text: 'Requisitions', icon: <FaClipboardList />, link: '/StoreKeeperViewRequisition' },
        { text: 'Purchase Order', icon: <FaShoppingCart />, link: '/PurchaseRequests' },
      ]
    },
    {
      category: "Leave Management",
      icon: <FaCalendarAlt />,
      items: [
        { text: 'Leave Request', icon: <FaCalendarAlt />, link: '/LeaveCalendar_Storekeeper' },
      ]
    }
  ];

  const toggleDrawer = () => setDrawerOpen(!drawerOpen);
  const handleMenuItemClick = () => setDrawerOpen(false);

  const handleGroupToggle = (category) => {
    setOpenGroups(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const renderMenuItems = () => (
    <List sx={{ px: 1 }}>
      {menuGroups.map((group) => {
        const isGroupOpen = openGroups[group.category] !== false;
        
        return (
          <Box key={group.category}>
            {/* Category Header */}
            <ListItem
              button
              onClick={() => handleGroupToggle(group.category)}
              sx={{
                backgroundColor: '#1E2A32',
                borderRadius: '12px',
                marginY: 1,
                paddingY: 1.5,
                paddingX: 2,
                '&:hover': {
                  backgroundColor: '#2A3A44',
                },
              }}
            >
              <Box sx={{ marginRight: 2, fontSize: '1.2rem', color: '#64B5F6' }}>
                {group.icon}
              </Box>
              <ListItemText
                primary={group.category}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  color: '#B0BEC5',
                }}
              />
              {isGroupOpen ? 
                <MdExpandLess color="#B0BEC5" /> : 
                <MdExpandMore color="#B0BEC5" />
              }
            </ListItem>
            
            {/* Menu Items in Category */}
            <Collapse in={isGroupOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {group.items.map((item) => {
                  const isSelected = location.pathname === item.link;
                  return (
                    <ListItem
                      button
                      component={Link}
                      to={item.link}
                      key={item.text}
                      onClick={handleMenuItemClick}
                      sx={{
                        backgroundColor: isSelected ? '#37474F' : 'transparent',
                        boxShadow: isSelected ? '0px 4px 12px rgba(0, 0, 0, 0.4)' : 'none',
                        border: isSelected ? '1px solid #546E7A' : '1px solid transparent',
                        borderRadius: '12px',
                        paddingY: 1.5,
                        paddingX: 2,
                        marginY: 0.5,
                        marginLeft: 2,
                        marginRight: 1,
                        color: isSelected ? '#ECEFF1' : '#B0BEC5',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#37474F',
                          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
                          border: '1px solid #546E7A',
                          color: '#FFFFFF',
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      <Box sx={{ 
                        marginRight: 2, 
                        fontSize: '1.2rem',
                        color: isSelected ? '#64B5F6' : '#78909C',
                      }}>
                        {item.icon}
                      </Box>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          fontWeight: isSelected ? 600 : 400,
                        }}
                      />
                      {isSelected && (
                        <Box
                          sx={{
                            width: 3,
                            height: 20,
                            backgroundColor: '#64B5F6',
                            borderRadius: 2,
                            ml: 1,
                          }}
                        />
                      )}
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </List>
  );

  return (
    <>
      {/* Mobile menu button */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1301,
          display: { xs: 'block', sm: 'none' },
        }}
      >
        <IconButton 
          color="inherit" 
          onClick={toggleDrawer} 
          aria-label="Open menu"
          sx={{
            backgroundColor: '#263238',
            '&:hover': { backgroundColor: '#37474F' },
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          <MenuIcon sx={{ color: 'white' }} />
        </IconButton>
      </Box>

      {/* Drawer for mobile */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: '#263238',
            color: 'white',
            boxShadow: '4px 0 20px rgba(0,0,0,0.3)',
          },
        }}
      >
        <Box
          sx={{
            width: 280,
            height: '100%',
            backgroundColor: '#263238',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#1A252F',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#546E7A',
              borderRadius: '3px',
            },
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              textAlign: 'center',
              paddingY: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              borderBottom: '1px solid #37474F',
            }}
          >
            <img src={logo} alt="Logo" style={{ width: 60, height: 60, marginBottom: 8 }} />
            <Typography
              variant="h6"
              sx={{
                fontSize: '1rem',
                fontWeight: 'bold',
                color: '#64B5F6',
                letterSpacing: '1px',
              }}
            >
              Store Keeper
            </Typography>
          </Box>
          {renderMenuItems()}
        </Box>
      </Drawer>

      {/* Sidebar for desktop */}
      <Box
        sx={{
          width: 280,
          minHeight: '100vh',
          backgroundColor: '#263238',
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column',
          color: 'white',
          position: 'sticky',
          top: 0,
          left: 0,
          boxShadow: '4px 0 20px rgba(0,0,0,0.2)',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#1A252F',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#546E7A',
            borderRadius: '3px',
          },
        }}
      >
        {/* Logo and title */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 3,
            mb: 2,
            borderBottom: '1px solid #37474F',
          }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{
              width: 80,
              height: 'auto',
              marginBottom: 12,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 'bold',
              color: '#64B5F6',
              letterSpacing: '1px',
            }}
          >
            Store Keeper Portal
          </Typography>
        </Box>
        {renderMenuItems()}
      </Box>
    </>
  );
}

export default Sidebar;